import {
  DialogPopupTypes,
  SelectOption,
  Constraints
} from '@touche-lts/panel-ui/dist'
import { makeAutoObservable } from 'mobx'
import { UserRoleDTO, UserRoleName } from 'models'
import { Api } from 'services'
// import { RootStore } from 'mobx-stores/root-store'
import { validate } from 'validate.js'
import { UserRequest } from './add-user-page-store.types'
import {
  initialUserRequest,
  userRequestConstraints
} from './add-user-page-store.utils'

export class AddUserPageStore {
  private userApi: Api['userApi']
  private rootStore: any

  constructor (rootStore: any) {
    this.userApi = rootStore.generalStore.api.userApi
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  // OBSERVABLES
  public userRequest: UserRequest = initialUserRequest
  public validationErrors: Constraints<UserRequest> = {}

  // ACTIONS
  private setUserRequestProps = (props: Partial<UserRequest>): void => {
    this.userRequest = {
      ...this.userRequest,
      ...props
    }
  }

  private setValidationErrors = (
    validationsErrors: Constraints<UserRequest>
  ): void => {
    this.validationErrors = validationsErrors
  }

  // METHODS
  public clearStore = (): void => {
    this.setUserRequestProps(initialUserRequest)
    this.setValidationErrors({})
  }

  public onAddUserPageMount = async (): Promise<void> => {
    await this.rootStore.generalStore.loadUserRoles()
  }

  public onUserRequestPropsChange = (props: Partial<UserRequest>): void => {
    const validationErrors = { ...this.validationErrors }
    Object.keys(props).forEach(key => {
      delete validationErrors[key]
    })

    this.setValidationErrors(validationErrors)
    this.setUserRequestProps(props)
  }

  public onFullNameChange = (fullName: string): void => {
    const validationErrors = { ...this.validationErrors }
    delete validationErrors.firstName
    delete validationErrors.lastName
    this.setValidationErrors(validationErrors)
    this.setUserRequestProps({ fullName })
  }

  private handleFullName = (): void => {
    const [firstName, ...rest] = this.userRequest.fullName.trim().split(' ')
    const lastName = rest.join(' ').trim() || ''

    this.setUserRequestProps({ firstName, lastName })
  }

  public submit = async (): Promise<void> => {
    this.handleFullName()
    const validationErrors = validate(this.userRequest, userRequestConstraints)

    if (validationErrors) {
      this.setValidationErrors(validationErrors)
      return
    }

    const userDto = await this.userApi.saveUser(this.userRequest as any)

    if (!userDto) return

    this.rootStore.routingStore.push('/user-management')
    this.clearStore()
    this.rootStore.generalStore.showSuccess('User added successfully')
  }

  public onDiscardChangesClick = (): void => {
    const {
      generalStore: { openDialogPopup, closeDialogPopup },
      routingStore: { push }
    } = this.rootStore

    openDialogPopup({
      type: DialogPopupTypes.WARNING,
      buttons: [
        {
          children: 'Proceed',
          preset: 'primary',
          onClick: () => {
            push('/user-management')
            closeDialogPopup()
          }
        }
      ],
      closeButtonLabel: 'Cancel',
      message: 'Changes you have made will be lost!'
    })
  }

  // COMPUTED
  public get selectedUserRole (): UserRoleDTO {
    return this.userRequest.role || ('' as any)
  }

  public get userRoleSelectOptions (): SelectOption<UserRoleDTO>[] {
    const { userRoleSelectOptions } = this.rootStore.generalStore
    return userRoleSelectOptions.filter(
      role => role.value.name !== UserRoleName.VIEWER && role.value.name !== UserRoleName.SALES_ASSISTANT // TODO: Remove because we don't have sales assistant yet
    )
  }
}
